import { NgIf } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbHighlight, NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { Observable, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  tap,
} from 'rxjs/operators';
import { EntityType } from 'src/app/core/settings/settings.service';
import { Company } from '../../settings/request-creation-modal/models/company';
import { EntityPathPipe } from './entity-path.pipe';
import { Entity, EntitySearchService } from './entity-search.service';

@Component({
  selector: 'dcc-entity-search',
  templateUrl: './entity-search.component.html',
  styleUrls: ['./entity-search.component.scss'],
  standalone: true,
  imports: [NgbHighlight, EntityPathPipe, FormsModule, NgbTypeahead, NgIf],
})
export class EntitySearchComponent implements OnInit, OnChanges {
  @Input()
  disable = false;

  @Input()
  company: Company;

  @Input()
  entityTypes: EntityType[];

  @Input()
  clearTextOnSelect = false;

  @Output()
  entity: EventEmitter<Entity> = new EventEmitter<Entity>();

  @Output()
  clearEntity: EventEmitter<void> = new EventEmitter<void>();

  placeholderText: string;
  searchFailed: boolean;
  entities: Entity[];
  searchTerm = '';
  selectedEntity = false;
  errorMessage = '';
  searchEntityBinding: (searched: Observable<string>) => Observable<Entity[]>;

  constructor(private readonly entitySearchService: EntitySearchService) {}

  get disableInternal(): boolean {
    return this.disable || this.selectedEntity;
  }

  ngOnChanges(): void {
    this.placeholderText = this.getPlaceholderText();
  }

  ngOnInit(): void {
    this.searchEntityBinding = this.searchEntities.bind(this);
    this.placeholderText = this.getPlaceholderText();
  }

  searchEntities(searched: Observable<string>): Observable<Entity[]> {
    return searched.pipe(
      tap(() => (this.searchFailed = false)),
      debounceTime(300),
      filter((term) => term.length >= 3),
      distinctUntilChanged(),
      tap(() => (this.entities = [])),
      switchMap((term) =>
        this.entitySearchService
          .searchEntitiesObservable(term, this.company?.id, this.entityTypes)
          .pipe(
            tap(() => (this.searchFailed = false)),
            tap((entities) => this.parseResults(entities)),
            catchError(() => {
              this.entities = [];
              this.searchFailed = true;
              this.errorMessage = `An error occurred searching for entities.`;
              return of([]);
            }),
          ),
      ),
    );
  }

  clear() {
    this.selectedEntity = false;
    this.clearEntity.emit();

    this.searchTerm = '';
  }

  onSelectItem(selectedItem: any) {
    this.selectedEntity = true;
    this.entity.emit(selectedItem.item);

    if (this.clearTextOnSelect) {
      this.selectedEntity = false;
      this.searchTerm = '';
    }
  }

  formatter = (e: Entity) => {
    if (!this.selectedEntity) {
      return '';
    }
    return e.name;
  };

  getIcon(entityType: string): string {
    return '../../assets/images/settings/' + entityType.toLowerCase() + '.svg';
  }

  private getPlaceholderText() {
    const typeBasedPlaceholder = this.entityTypes?.reduce(
      (previous, value, index, array) => {
        if (index === 0) {
          previous = 'Search ';
        }

        // If only item
        if (array.length === 1) {
          return previous + value;
        }

        // If last item
        if (array.length === index + 1) {
          return previous + `or ${value}`;
        }

        if (array.length === index + 2) {
          return previous + `${value} `;
        }

        return previous + `${value}, `;
      },
      '',
    );

    const defaultPlaceholder = this.company
      ? 'Search Group, Vehicle, or Device'
      : 'Search Company, Group, Vehicle, or Device';
    return typeBasedPlaceholder ?? defaultPlaceholder;
  }

  private parseResults(results: Entity[]) {
    if (results.length === 0) {
      this.searchFailed = true;
      this.errorMessage = 'No results found';
      return;
    }

    this.entities = results;
  }
}
